/**
 * ProjectVerify — /verify/project/:token
 * Public page for external independent verifiers.
 * No platform authentication required — token-based access only.
 *
 * Shows:
 *   - Project summary (name, sector, org, methodology)
 *   - Calculation outputs (tCO₂e/yr, energy, DSCR)
 *   - Evidence list (uploaded/verified items per section)
 *   - Approve / Reject decision form
 *
 * Rules:
 *   - Verifier must NOT be the same email as the project owner
 *   - Decision is final for this token
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  RefreshCw, CheckCircle2, XCircle, AlertCircle, Shield,
  FileText, AlertTriangle, ChevronDown, ChevronUp,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png";

// ── Status badge ──────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const CFG = {
    missing:      { label: "Not Uploaded", cls: "bg-slate-100 text-slate-500" },
    uploaded:     { label: "Uploaded",     cls: "bg-amber-100 text-amber-700" },
    under_review: { label: "Under Review", cls: "bg-blue-100 text-blue-700" },
    verified:     { label: "Verified",     cls: "bg-emerald-100 text-emerald-700" },
    rejected:     { label: "Rejected",     cls: "bg-red-100 text-red-700" },
    needs_clarification: { label: "Needs Clarification", cls: "bg-orange-100 text-orange-700" },
  };
  const c = CFG[status] || CFG.missing;
  return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.cls}`}>{c.label}</span>;
}

// ── Evidence section card ──────────────────────────────────────────────────
function EvidenceSection({ sectionName, items }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-3">
      <button className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 text-left" onClick={() => setOpen(v => !v)}>
        <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">{sectionName}</p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">{items.filter(i => i.status === "verified").length}/{items.length} verified</span>
          {open ? <ChevronUp size={12} className="text-slate-400" /> : <ChevronDown size={12} className="text-slate-400" />}
        </div>
      </button>
      {open && (
        <div className="divide-y divide-slate-50">
          {items.map(item => (
            <div key={item.parameter} className="px-4 py-3 flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <p className="text-xs font-semibold text-slate-800">{item.label}</p>
                  {item.critical && <span className="text-xs bg-red-100 text-red-600 px-1.5 rounded font-bold">CRITICAL</span>}
                </div>
                {item.file_name && <p className="text-xs text-slate-400 font-mono">{item.file_name}</p>}
              </div>
              <StatusBadge status={item.status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProjectVerify() {
  const { token } = useParams();
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [submitting,setSubmitting]= useState(false);
  const [decided,   setDecided]   = useState(false);
  const [decisionResult, setDecisionResult] = useState(null);

  // Decision form state
  const [decision,    setDecision]    = useState("");
  const [name,        setName]        = useState("");
  const [org,         setOrg]         = useState("");
  const [email,       setEmail]       = useState("");
  const [comment,     setComment]     = useState("");
  const [rejectReason,setRejectReason]= useState("");

  useEffect(() => {
    if (!token) { setError("No verification token provided."); setLoading(false); return; }
    fetch(`${API}/api/public/workspace/project-verify/${token}`)
      .then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.detail?.message || "Not found"); }); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [token]);

  const handleDecide = async () => {
    if (!decision || !name || !email) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/public/workspace/project-verify/${token}/decide`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          decision,
          verifier_name:    name,
          verifier_org:     org || null,
          verifier_email:   email,
          comment:          comment || null,
          rejection_reason: rejectReason || null,
        }),
      });
      const resp = await res.json();
      if (!res.ok) throw new Error(resp.detail?.message || "Decision failed");
      setDecisionResult(resp);
      setDecided(true);
    } catch (e) { setError(e.message); }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
      <RefreshCw size={24} className="text-teal-400 animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
        <AlertCircle size={32} className="text-red-500 mx-auto mb-3" />
        <p className="text-sm font-bold text-slate-900 mb-1">Verification Link Error</p>
        <p className="text-xs text-slate-500">{error}</p>
      </div>
    </div>
  );

  // Decided state
  if (decided && decisionResult) return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
        {decisionResult.decision === "approved"
          ? <CheckCircle2 size={48} className="text-emerald-500 mx-auto mb-4" />
          : <XCircle size={48} className="text-red-500 mx-auto mb-4" />}
        <p className="text-lg font-bold text-slate-900 mb-2">
          {decisionResult.decision === "approved" ? "Verification Approved" : "Verification Rejected"}
        </p>
        <p className="text-sm text-slate-600 mb-4">{decisionResult.message}</p>
        {decisionResult.tier_upgraded && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
            <p className="text-xs font-bold text-emerald-800">Project status updated: Calculated Estimate</p>
          </div>
        )}
        <p className="text-xs text-slate-400 mt-4">Your decision has been recorded. You may close this window.</p>
      </div>
    </div>
  );

  // Already decided
  if (data?.is_closed) return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
        <Shield size={32} className={`mx-auto mb-4 ${data.prior_decision?.decision === "approved" ? "text-emerald-500" : "text-slate-400"}`} />
        <p className="text-sm font-bold text-slate-900 mb-1">Verification {data.prior_decision?.decision === "approved" ? "Approved" : "Closed"}</p>
        <p className="text-xs text-slate-500">A decision was already submitted by {data.prior_decision?.verifier_name} ({data.prior_decision?.decided_at?.slice(0,10)}).</p>
      </div>
    </div>
  );

  const proj = data.project || {};
  const calc = data.calculation || {};
  const ev   = data.evidence || [];
  const evSum= data.evidence_summary || {};

  // Group evidence by section
  const sections = {};
  ev.forEach(item => {
    const sec = item.section || "General";
    if (!sections[sec]) sections[sec] = [];
    sections[sec].push(item);
  });

  return (
    <div className="min-h-screen bg-[#0F172A]">
      {/* Header */}
      <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
        <img src={LOGO_URL} alt="DeCarbonIQ" className="h-7" onError={e => { e.target.style.display="none"; }} />
        <span className="text-xs text-teal-400 font-bold px-3 py-1.5 bg-teal-900/30 border border-teal-700 rounded-full">
          Independent Verification Review
        </span>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Status banner */}
        <div className="bg-amber-900/20 border border-amber-500/40 rounded-xl px-5 py-4 flex items-center gap-3">
          <AlertTriangle size={16} className="text-amber-400 flex-shrink-0" />
          <div>
            <p className="text-sm font-bold text-amber-300">Independent Verification Required</p>
            <p className="text-xs text-amber-400 mt-0.5">
              You have been invited to independently verify this project. Review the evidence and outputs, then submit your decision.
              Your identity will be recorded as part of the audit trail.
            </p>
          </div>
        </div>

        {/* Project overview */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <p className="text-xs text-slate-400 uppercase tracking-widest mb-1">{proj.org_name}</p>
          <h1 className="text-2xl font-bold text-white mb-1">{proj.name}</h1>
          <p className="text-sm text-slate-400">{proj.sector_label} · {proj.methodology} · {proj.country}</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
            {[
              ["Annual Reduction", calc.annual_reduction_tco2e > 0 ? `${Number(calc.annual_reduction_tco2e).toFixed(1)} tCO₂e/yr` : "—"],
              ["Electricity Generated", calc.energy_mwh_yr > 0 ? `${calc.energy_mwh_yr.toLocaleString()} MWh/yr` : "—"],
              ["DSCR", calc.dscr != null ? `${calc.dscr}x` : "—"],
              ["Reduction %", calc.reduction_pct != null ? `${calc.reduction_pct}%` : "—"],
            ].map(([l, v]) => (
              <div key={l} className="bg-white/5 rounded-xl px-3 py-2.5">
                <p className="text-xs text-slate-400">{l}</p>
                <p className="text-sm font-bold text-white">{v}</p>
              </div>
            ))}
          </div>

          {/* Baseline + project scenario */}
          {calc.baseline_scenario && (
            <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-400 uppercase mb-1">Baseline Scenario</p>
                <p className="text-xs text-slate-300 leading-relaxed">{calc.baseline_scenario}</p>
                <p className="text-xs mt-1">
                  <span className={`font-semibold ${calc.baseline_confirmed ? "text-emerald-400" : "text-amber-400"}`}>
                    Baseline: {calc.baseline_confirmed ? "Confirmed" : "Assumed default"}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase mb-1">Calculation Version</p>
                <p className="text-xs text-slate-300">{calc.version_label}</p>
                <p className="text-xs text-slate-400 mt-0.5">Calculated: {calc.calculated_at?.slice(0,10)}</p>
              </div>
            </div>
          )}
        </div>

        {/* Evidence */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-bold text-white">Evidence Review</p>
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${
              evSum.verified === evSum.total ? "bg-emerald-900/40 text-emerald-400 border border-emerald-700" :
              "bg-amber-900/30 text-amber-400 border border-amber-700"
            }`}>
              {evSum.verified}/{evSum.total} verified · {evSum.uploaded} uploaded
            </span>
          </div>
          {evSum.critical_missing?.length > 0 && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-xl px-4 py-3 mb-3">
              <p className="text-xs text-red-400 font-semibold">Critical evidence not uploaded: {evSum.critical_missing.join(", ")}</p>
            </div>
          )}
          {Object.entries(sections).map(([sec, items]) => (
            <EvidenceSection key={sec} sectionName={sec} items={items} />
          ))}
        </div>

        {/* Decision form */}
        <div className="bg-white rounded-2xl p-6 space-y-4" data-testid="decision-form">
          <p className="text-sm font-bold text-slate-900">Your Verification Decision</p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <p className="text-xs text-amber-800">
              <strong>Registry requirement:</strong> Only registered verifiers in the DeCarbonIQ accredited verifier registry
              can submit decisions. Your email address must match your registry record. Anonymous verification is not permitted.
            </p>
            <p className="text-xs text-amber-700 mt-1">
              Not registered? Contact the platform administrator at admin@decarboniq.com to onboard your organization.
            </p>
          </div>

          {/* Verifier identity */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Full Name <span className="text-red-400">*</span></label>
              <input type="text" value={name} onChange={e => setName(e.target.value)}
                placeholder="Dr. Fatima Abubakar" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Organization</label>
              <input type="text" value={org} onChange={e => setOrg(e.target.value)}
                placeholder="EnviroAudit Nigeria Ltd" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-700 block mb-1">Email Address <span className="text-red-400">*</span></label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="verifier@independent-firm.com" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400" />
            </div>
          </div>

          {/* Decision choice */}
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => setDecision("approved")}
              className={`py-3 rounded-xl text-sm font-bold border-2 transition-all ${
                decision === "approved" ? "bg-emerald-600 border-emerald-600 text-white" : "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
              }`} data-testid="decision-approve">
              <CheckCircle2 size={16} className="inline mr-2" />
              Approve — Mark as Independently Reviewed
            </button>
            <button onClick={() => setDecision("rejected")}
              className={`py-3 rounded-xl text-sm font-bold border-2 transition-all ${
                decision === "rejected" ? "bg-red-600 border-red-600 text-white" : "bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
              }`} data-testid="decision-reject">
              <XCircle size={16} className="inline mr-2" />
              Reject — Needs Clarification
            </button>
          </div>

          {/* Rejection reason */}
          {decision === "rejected" && (
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Rejection Reason <span className="text-red-400">*</span></label>
              <input type="text" value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                placeholder="Insufficient baseline evidence, methodology deviation..." className="w-full px-3 py-2 text-sm border border-red-200 rounded-xl focus:outline-none focus:border-red-400" />
            </div>
          )}

          {/* Comment */}
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Review Comment</label>
            <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3}
              placeholder="Describe the basis of your decision. Reference specific evidence documents reviewed..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400 resize-none" />
          </div>

          {/* Submit */}
          <button onClick={handleDecide}
            disabled={!decision || !name || !email || submitting || (decision === "rejected" && !rejectReason)}
            className={`w-full py-3.5 rounded-xl text-sm font-bold transition-all ${
              decision === "approved" ? "bg-emerald-600 hover:bg-emerald-700 text-white" :
              decision === "rejected" ? "bg-red-600 hover:bg-red-700 text-white" :
              "bg-slate-100 text-slate-400 cursor-not-allowed"
            } disabled:opacity-50`}
            data-testid="submit-decision">
            {submitting ? <><RefreshCw size={14} className="inline animate-spin mr-2" />Submitting...</> :
             decision === "approved" ? "Submit: Approve Independent Verification" :
             decision === "rejected" ? "Submit: Reject with Clarification Required" :
             "Select a decision to continue"}
          </button>

          <p className="text-xs text-slate-400 text-center italic">
            This decision is final and will be recorded in the project audit trail.
          </p>
        </div>
      </div>
    </div>
  );
}
